import { START_NODE_ID } from './nodeFactory';

// WhatsApp Cloud API limits (mirror of backend Flows::SchemaValidator)
const MAX_LIST_ROWS = 10;
const MAX_BUTTONS = 3;
const MAX_ROW_TITLE = 24;
const MAX_ROW_DESCRIPTION = 72;
const MAX_BUTTON_TITLE = 20;
const MAX_BUTTON_TEXT = 20;

// frontend validation: instant feedback; the backend validator remains the
// final authority on publish/activate
export default function validateCanvas(nodes, edges, t) {
  const errors = [];
  const warnings = [];

  const hasEdge = (source, sourceHandle) =>
    edges.some(e => e.source === source && e.sourceHandle === sourceHandle);

  const startEdge = edges.find(e => e.source === START_NODE_ID);
  if (!startEdge) {
    errors.push(t('FLOWS.BUILDER.VALIDATION.NO_START'));
  }

  const flowNodes = nodes.filter(node => node.id !== START_NODE_ID);
  if (!flowNodes.length) {
    errors.push(t('FLOWS.BUILDER.VALIDATION.EMPTY'));
    return { errors, warnings };
  }

  flowNodes.forEach(node => {
    const title = node.data?.title || node.id;
    const config = node.data?.config || {};

    if (node.type === 'message' && !String(config.text || '').trim()) {
      errors.push(t('FLOWS.BUILDER.VALIDATION.MESSAGE_EMPTY', { title }));
    }

    if (node.type === 'whatsapp_list') {
      if (!String(config.body || '').trim()) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.BODY_EMPTY', { title }));
      }
      if (String(config.button_text || '').length > MAX_BUTTON_TEXT) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.BUTTON_TEXT_LIMIT', { title, max: MAX_BUTTON_TEXT }));
      }
      const rows = (config.sections || []).flatMap(s => s.rows || []);
      if (!rows.length) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.LIST_NO_ROWS', { title }));
      }
      if (rows.length > MAX_LIST_ROWS) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.LIST_ROW_LIMIT', { title, max: MAX_LIST_ROWS }));
      }
      const ids = rows.map(r => r.id);
      if (ids.some(id => !id) || new Set(ids).size !== ids.length) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.DUPLICATE_OPTION_ID', { title }));
      }
      rows.forEach(row => {
        if (!String(row.title || '').trim() || String(row.title).length > MAX_ROW_TITLE) {
          errors.push(t('FLOWS.BUILDER.VALIDATION.ROW_TITLE_INVALID', { title, option: row.id, max: MAX_ROW_TITLE }));
        }
        if (String(row.description || '').length > MAX_ROW_DESCRIPTION) {
          errors.push(t('FLOWS.BUILDER.VALIDATION.ROW_DESCRIPTION_LIMIT', { title, option: row.id, max: MAX_ROW_DESCRIPTION }));
        }
        if (!hasEdge(node.id, `row-${row.id}`)) {
          errors.push(t('FLOWS.BUILDER.VALIDATION.OPTION_NOT_CONNECTED', { title, option: row.title || row.id }));
        }
      });
      if (!hasEdge(node.id, 'fallback')) {
        warnings.push(t('FLOWS.BUILDER.VALIDATION.NO_FALLBACK', { title }));
      }
    }

    if (node.type === 'buttons') {
      if (!String(config.body || '').trim()) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.BODY_EMPTY', { title }));
      }
      const buttons = config.buttons || [];
      if (!buttons.length) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.NO_BUTTONS', { title }));
      }
      if (buttons.length > MAX_BUTTONS) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.BUTTONS_LIMIT', { title, max: MAX_BUTTONS }));
      }
      const ids = buttons.map(b => b.id);
      if (ids.some(id => !id) || new Set(ids).size !== ids.length) {
        errors.push(t('FLOWS.BUILDER.VALIDATION.DUPLICATE_OPTION_ID', { title }));
      }
      buttons.forEach(button => {
        if (!String(button.title || '').trim() || String(button.title).length > MAX_BUTTON_TITLE) {
          errors.push(t('FLOWS.BUILDER.VALIDATION.BUTTON_TITLE_INVALID', { title, option: button.id, max: MAX_BUTTON_TITLE }));
        }
        if (!hasEdge(node.id, `btn-${button.id}`)) {
          errors.push(t('FLOWS.BUILDER.VALIDATION.OPTION_NOT_CONNECTED', { title, option: button.title || button.id }));
        }
      });
      if (!hasEdge(node.id, 'fallback')) {
        warnings.push(t('FLOWS.BUILDER.VALIDATION.NO_FALLBACK', { title }));
      }
    }
    if (node.type === 'media' && !config.media_asset_id) {
      errors.push(t('FLOWS.BUILDER.VALIDATION.MEDIA_NO_ASSET', { title }));
    }
  });

  // unreachable nodes (warning)
  if (startEdge) {
    const adjacency = {};
    edges.forEach(edge => {
      adjacency[edge.source] = adjacency[edge.source] || [];
      adjacency[edge.source].push(edge.target);
    });
    const reachable = new Set();
    const queue = [startEdge.target];
    while (queue.length) {
      const id = queue.shift();
      if (reachable.has(id)) continue;
      reachable.add(id);
      (adjacency[id] || []).forEach(next => queue.push(next));
    }
    flowNodes.forEach(node => {
      if (!reachable.has(node.id)) {
        warnings.push(t('FLOWS.BUILDER.VALIDATION.UNREACHABLE', { title: node.data?.title || node.id }));
      }
    });
  }

  return { errors, warnings };
}
